import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * ResponsiveImage Component (Web Implementation)
 *
 * Web implementation of ResponsiveImage using Next.js Image optimization
 * with lazy loading, blur placeholders, and error handling.
 * Note: "use client" is not used here; this package is consumed by both
 * Next.js (which can mark the importer as client) and Storybook (Vite).
 */
import { useState, useCallback, useRef, useEffect } from 'react';
import { calculateAspectRatioPadding } from './ResponsiveImage.types';
import { handleImageError, getAspectRatioContainerStyles, getAspectRatioImageStyles, validateImageSrc, } from './imageUtils';
// Conditionally import Next.js Image to avoid errors when not available.
// Extracted to a separate module so vi.mock() can intercept it in tests
// (vitest cannot intercept bare CJS require() calls for external packages).
import { NextImage } from './nextImageLoader';
// Type guard to check if NextImage is a valid React component
const isValidNextImage = (nextImage) => {
    return (nextImage !== null &&
        typeof nextImage === 'object' &&
        ('default' in nextImage || typeof nextImage === 'function'));
};
export const ResponsiveImage = ({ src, alt, aspectRatio = '16:9', blurDataURL, fallbackSrc, lazy = true, priority = false, className, onLoad, onError, width, height, quality = 75, fill = true, objectFit = 'cover', objectPosition, placeholder = 'blur', 'data-testid': testId, }) => {
    const [imageState, setImageState] = useState({
        loaded: false,
        error: false,
        isInView: !lazy, // If not lazy, consider it in view
    });
    const containerRef = useRef(null);
    const observerRef = useRef(null);
    // Setup intersection observer for lazy loading
    useEffect(() => {
        if (!lazy || imageState.isInView || priority)
            return;
        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    setImageState((prev) => ({ ...prev, isInView: true }));
                    observer.disconnect();
                }
            });
        }, { rootMargin: '50px', threshold: 0.1 });
        if (containerRef.current) {
            observer.observe(containerRef.current);
        }
        observerRef.current = observer;
        return () => {
            observer.disconnect();
        };
    }, [lazy, imageState.isInView, priority]);
    // Handle successful load
    const handleLoad = useCallback(() => {
        setImageState((prev) => ({ ...prev, loaded: true, error: false }));
        onLoad?.();
    }, [onLoad]);
    // Handle load error
    const handleError = useCallback((event) => {
        setImageState((prev) => ({ ...prev, error: true }));
        handleImageError(event, fallbackSrc, onError);
    }, [fallbackSrc, onError]);
    // Validate props (after all hooks to satisfy rules of hooks)
    if (!validateImageSrc(src)) {
        console.warn('ResponsiveImage: Invalid src provided');
        return null;
    }
    if (!alt || alt.trim() === '') {
        console.warn('ResponsiveImage: alt prop is required for accessibility');
    }
    // Image styles
    const imageStyles = {
        ...getAspectRatioImageStyles(),
        objectFit,
        objectPosition,
    };
    // Determine if we should render the image
    const shouldRenderImage = imageState.isInView || priority;
    // Use Next.js Image if available, otherwise use regular img
    const ImageComponent = isValidNextImage(NextImage)
        ? (NextImage.default || NextImage)
        : 'img';
    const commonImageProps = {
        src: shouldRenderImage ? src : '',
        alt,
        onLoad: handleLoad,
        onError: handleError,
        style: NextImage ? undefined : imageStyles,
        className,
        'data-testid': testId,
    };
    const nextImageProps = isValidNextImage(NextImage)
        ? {
            ...(fill
                ? { fill: true }
                : {
                    width: width || 800,
                    height: (() => {
                        const numericAspectRatio = calculateAspectRatioPadding(aspectRatio) / 100 + 1; // Convert padding % to ratio
                        return height || Math.round((width || 800) / numericAspectRatio);
                    })(),
                }),
            quality,
            style: {
                objectFit: objectFit || 'cover',
                objectPosition: objectPosition || 'center',
            },
            placeholder: blurDataURL ? 'blur' : placeholder,
            blurDataURL: blurDataURL || undefined,
            priority,
            loading: priority ? 'eager' : 'lazy',
            sizes: '(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw',
        }
        : {};
    return (_jsxs("div", { ref: containerRef, className: className, style: getAspectRatioContainerStyles(aspectRatio), "data-testid": testId ? `${testId}-container` : undefined, children: [isValidNextImage(NextImage) && shouldRenderImage ? (_jsx(ImageComponent, { ...commonImageProps, ...nextImageProps })) : shouldRenderImage ? (_jsx("img", { ...commonImageProps, style: imageStyles, loading: priority ? 'eager' : 'lazy' })) : (_jsx("div", { style: imageStyles, "aria-label": `Loading image: ${alt}`, role: "status" })), blurDataURL && !imageState.loaded && !imageState.error && (_jsx("div", { style: {
                    ...imageStyles,
                    backgroundImage: `url(${blurDataURL})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    filter: 'blur(10px)',
                    transform: 'scale(1.1)', // Prevent blur edges from showing
                }, "aria-hidden": "true" }))] }));
};
export default ResponsiveImage;
